import { Injectable } from '@angular/core';
import { InsightsService } from 't4core';

export interface MapLocation {
  latitude: number;
  longitude: number;
  radius?: number;
}

export interface MapConfig {
  userLocation: MapLocation;
  targetLocation: MapLocation;
  pinIconUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private map: google.maps.Map | null = null;
  private mapContainer: HTMLElement | null = null;
  private userMarker: google.maps.Marker | null = null;
  private targetMarker: google.maps.Marker | null = null;
  private targetCircle: google.maps.Circle | null = null;
  private currentPinIconUrl: string | null = null;

  /** Cache of map instances by source key */
  private mapInstanceCache: Map<string, google.maps.Map> = new Map();

  private readonly zoomDistances = [
    { zoom: 17, maxDistance: 0.2 },
    { zoom: 15, maxDistance: 0.6 },
    { zoom: 14, maxDistance: 2 },
    { zoom: 12, maxDistance: 4 },
    { zoom: 11, maxDistance: 8 },
    { zoom: 9, maxDistance: 16 },
    { zoom: 8, maxDistance: 32 },
    { zoom: 7, maxDistance: 64 },
    { zoom: 6, maxDistance: 128 },
    { zoom: 5, maxDistance: 256 },
    { zoom: 4, maxDistance: 512 },
    { zoom: 3, maxDistance: 1024 },
    { zoom: 2, maxDistance: 2048 },
    { zoom: 1, maxDistance: 4096 }
  ];

  constructor(private insightsService: InsightsService) {}

  /**
   * Gets or creates a cached map instance for the given source key.
   * The map instance is cached and reused when the same key is requested again.
   * @param sourceKey Unique identifier for the map instance (e.g., 'location-map', 'return-faraway')
   * @param container The HTML element to render the map in
   * @param options Google Maps options for creating the map
   * @returns The cached or newly created map instance
   */
  public getMapInstance(sourceKey: string, container: HTMLElement, options: any): any {
    let mapInstance = this.mapInstanceCache.get(sourceKey);

    if (mapInstance) {
      try {
        // Verify the cached map is still valid and its div is connected to the DOM
        const mapDiv = mapInstance.getDiv();
        if (mapDiv && document.body.contains(mapDiv)) {
          // Move existing map to new container
          container.appendChild(mapDiv);
          google.maps.event.trigger(mapInstance, 'resize');
          return mapInstance;
        }
        // Map div was removed from DOM (Angular destroyed it), invalidate cache
        this.mapInstanceCache.delete(sourceKey);
        this.insightsService.logEvent('GoogleMapsCacheFailed', { sourceKey: sourceKey });
      } catch (e) {
        // Cached map is invalid, remove from cache and create new one
        this.mapInstanceCache.delete(sourceKey);
        this.insightsService.logEvent('GoogleMapsCacheException', { sourceKey: sourceKey, exception: e });
      }
    }

    // Create new map instance
    mapInstance = new google.maps.Map(container, options);
    this.mapInstanceCache.set(sourceKey, mapInstance);

    // Log map load event to Application Insights
    this.insightsService.logEvent('GoogleMapsLoaded', { sourceKey: sourceKey });

    return mapInstance;
  }

  /**
   * Checks if a map instance exists for the given source key
   */
  public hasMapInstance(sourceKey: string): boolean {
    return this.mapInstanceCache.has(sourceKey);
  }

  /**
   * Detaches a cached map instance from its current container to preserve it.
   * Call this in ngOnDestroy to prevent Angular from destroying the map div.
   * @param sourceKey The key of the cached map instance to detach
   */
  public detachMapInstance(sourceKey: string): void {
    const mapInstance = this.mapInstanceCache.get(sourceKey);
    if (mapInstance) {
      try {
        const mapDiv = mapInstance.getDiv();
        if (mapDiv && mapDiv.parentElement) {
          // Move to a hidden container to preserve the map
          if (!this.hiddenMapContainer) {
            this.hiddenMapContainer = document.createElement('div');
            this.hiddenMapContainer.style.position = 'absolute';
            this.hiddenMapContainer.style.left = '-9999px';
            this.hiddenMapContainer.style.top = '-9999px';
            this.hiddenMapContainer.style.width = '1px';
            this.hiddenMapContainer.style.height = '1px';
            this.hiddenMapContainer.style.overflow = 'hidden';
            document.body.appendChild(this.hiddenMapContainer);
          }
          this.hiddenMapContainer.appendChild(mapDiv);
        }
      } catch (e) {
        // Ignore errors during detach
      }
    }
  }

  /** Hidden container to preserve map divs when components are destroyed */
  private hiddenMapContainer: HTMLElement | null = null;

  /**
   * Attaches the map to a DOM element. Creates a new map if one doesn't exist,
   * or moves the existing map to the new container.
   */
  public attachMap(container: HTMLElement, config: MapConfig): void {
    if (!this.map) {
      // Create new map
      this.createMap(container, config);
    } else {
      // Move existing map to new container
      this.moveMapToContainer(container, config);
    }

    this.updateMarkers(config);
  }

  /**
   * Detaches the map from the current container (but keeps it in memory for reuse)
   */
  public detachMap(): void {
    // Clear markers but keep the map instance
    this.clearMarkers();
    this.mapContainer = null;
  }

  /**
   * Updates the user's position marker on the map
   */
  public updateUserPosition(location: MapLocation): void {
    if (!this.map || !this.userMarker) return;

    this.userMarker.setPosition({ 
      lat: location.latitude, 
      lng: location.longitude 
    });
  }

  /**
   * Updates both user and target markers and adjusts the view
   */
  public updateMarkers(config: MapConfig): void {
    if (!this.map) return;

    // Update or create user marker
    if (this.userMarker) {
      this.userMarker.setPosition({
        lat: config.userLocation.latitude,
        lng: config.userLocation.longitude
      });
    } else {
      this.userMarker = new google.maps.Marker({
        position: { lat: config.userLocation.latitude, lng: config.userLocation.longitude },
        draggable: false,
        map: this.map
      });
    }

    // Update or create target marker
    if (this.targetMarker) {
      this.targetMarker.setPosition({
        lat: config.targetLocation.latitude,
        lng: config.targetLocation.longitude
      });

      // Update icon if changed
      if (config.pinIconUrl && config.pinIconUrl !== this.currentPinIconUrl) {
        this.targetMarker.setIcon(config.pinIconUrl);
        this.currentPinIconUrl = config.pinIconUrl;
      }
    } else {
      this.targetMarker = new google.maps.Marker({
        position: { lat: config.targetLocation.latitude, lng: config.targetLocation.longitude },
        draggable: false,
        map: this.map
      });

      if (config.pinIconUrl) {
        this.targetMarker.setIcon(config.pinIconUrl);
        this.currentPinIconUrl = config.pinIconUrl;
      }
    }

    // Update or create circle
    if (config.targetLocation.radius) {
      if (this.targetCircle) {
        this.targetCircle.setRadius(config.targetLocation.radius);
      } else {
        this.targetCircle = new google.maps.Circle({
          strokeColor: "#ADD8E6",
          strokeOpacity: 0.8,
          fillColor: "#32CD32",
          strokeWeight: 1,
          map: this.map,
          fillOpacity: 0.35,
          radius: config.targetLocation.radius
        });
        this.targetCircle.bindTo('center', this.targetMarker, 'position');
      }
    }
  }

  /**
   * Centers the map between user and target locations with appropriate zoom
   */
  public centerMapBetweenPoints(userLocation: MapLocation, targetLocation: MapLocation): void {
    if (!this.map) return;

    const distance = this.calculateDistance(userLocation, targetLocation);
    const zoom = this.getZoomForDistance(distance);

    this.map.setZoom(zoom);

    const centerLat = (userLocation.latitude + targetLocation.latitude) / 2;
    const centerLng = (userLocation.longitude + targetLocation.longitude) / 2;

    setTimeout(() => {
      this.map?.panTo({ lat: centerLat, lng: centerLng });
    }, 100);
  }

  /**
   * Gets the appropriate zoom level for a given distance
   */
  public getZoomForDistance(distanceKm: number): number {
    const zoomEntry = this.zoomDistances.find(x => x.maxDistance > distanceKm);
    return zoomEntry?.zoom ?? 10;
  }

  /**
   * Calculates distance between two points in kilometers
   */
  public calculateDistance(point1: MapLocation, point2: MapLocation): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(point2.latitude - point1.latitude);
    const dLon = this.toRad(point2.longitude - point1.longitude);
    const lat1 = this.toRad(point1.latitude);
    const lat2 = this.toRad(point2.latitude);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  private createMap(container: HTMLElement, config: MapConfig): void {
    const distance = this.calculateDistance(config.userLocation, config.targetLocation);

    const mapOptions: google.maps.MapOptions = {
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      disableDefaultUI: true,
      mapId: '17c55b6aa3c322c5',
      zoom: this.getZoomForDistance(distance),
      center: { 
        lat: config.targetLocation.latitude, 
        lng: config.targetLocation.longitude 
      }
    };

    this.map = new google.maps.Map(container, mapOptions);
    this.mapContainer = container;

    // Log map load event to Application Insights
    this.insightsService.logEvent('GoogleMapsLoaded', { sourceKey: 'rental-box' });
  }

  private moveMapToContainer(container: HTMLElement, config: MapConfig): void {
    if (!this.map) return;

    // Get the map's div element
    const mapDiv = this.map.getDiv();

    // Move the map div to the new container
    container.appendChild(mapDiv);
    this.mapContainer = container;

    // Trigger resize to ensure the map renders correctly in new container
    google.maps.event.trigger(this.map, 'resize');

    // Update center and zoom for new location
    const distance = this.calculateDistance(config.userLocation, config.targetLocation);
    this.map.setZoom(this.getZoomForDistance(distance));
    this.map.setCenter({ 
      lat: config.targetLocation.latitude, 
      lng: config.targetLocation.longitude 
    });
  }

  private clearMarkers(): void {
    if (this.userMarker) {
      this.userMarker.setMap(null);
      this.userMarker = null;
    }
    if (this.targetMarker) {
      this.targetMarker.setMap(null);
      this.targetMarker = null;
    }
    if (this.targetCircle) {
      this.targetCircle.setMap(null);
      this.targetCircle = null;
    }
    this.currentPinIconUrl = null;
  }
}
